import { useState } from "react";
import { useParams } from "react-router";
import { Button, Select, Input, Table, Popconfirm } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useTrialPlans } from "@/hooks/useTrialPlans";
import type { PlanVendor, VendorType, VendorStatus } from "./types";

const VENDOR_TYPES: VendorType[] = [
  "CRO",
  "Technology",
  "Central Lab",
  "Data Management",
  "Medical Writing",
  "Other",
];
const STATUS_OPTIONS: { value: VendorStatus; label: string }[] = [
  { value: "considering", label: "Considering" },
  { value: "selected", label: "Selected" },
  { value: "contracted", label: "Contracted" },
];
const STATUS_STYLE: Record<VendorStatus, string> = {
  considering: "bg-[#F3F4F6] text-[#6B7280]",
  selected: "bg-[#D8EEF8] text-[#196A9F]",
  contracted: "bg-[#CFE5D7] text-[#486563]",
};
const TYPE_STYLE: Record<VendorType, string> = {
  CRO: "bg-[#E0DEF0] text-[#59528F]",
  Technology: "bg-[#D8EEF8] text-[#196A9F]",
  "Central Lab": "bg-[#F8EEE1] text-[#C06119]",
  "Data Management": "bg-[#CFE5D7] text-[#486563]",
  "Medical Writing": "bg-[#E7D2DF] text-[#8C2760]",
  Other: "bg-[#F3F4F6] text-[#6B7280]",
};

const EMPTY_VENDOR: Omit<PlanVendor, "id"> = {
  name: "",
  type: "CRO",
  services: "",
  status: "considering",
  contact: "",
  notes: "",
};

function InlineEdit({
  value,
  onSave,
  placeholder,
  width = 150,
}: {
  value: string;
  onSave: (v: string) => void;
  placeholder?: string;
  width?: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          size="small"
          value={draft}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={() => {
            onSave(draft);
            setEditing(false);
          }}
          style={{ width, fontSize: 12 }}
        />
        <Button
          size="small"
          type="text"
          icon={<CheckOutlined />}
          className="text-[#0C98D0]"
          onClick={() => {
            onSave(draft);
            setEditing(false);
          }}
        />
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined />}
          className="text-[#9CA3AF]"
          onClick={() => {
            setDraft(value);
            setEditing(false);
          }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className="flex items-center gap-1 text-left group"
    >
      {value ? (
        <span className="text-[13px] text-[#1F2933]">{value}</span>
      ) : (
        <span className="text-[12px] text-[#D1D5DB] italic">
          {placeholder ?? "—"}
        </span>
      )}
      <EditOutlined className="text-[10px] text-[#D1D5DB] group-hover:text-[#9CA3AF] opacity-0 group-hover:opacity-100 shrink-0" />
    </button>
  );
}

export default function TrialPlanningVendors() {
  const { planId } = useParams<{ planId: string }>();
  const { getPlan, updatePlan } = useTrialPlans();
  const plan = planId ? getPlan(planId) : undefined;

  const [addingRow, setAddingRow] = useState(false);
  const [newVendor, setNewVendor] =
    useState<Omit<PlanVendor, "id">>(EMPTY_VENDOR);

  if (!plan) return null;

  const vendors = plan.vendors ?? [];
  const save = (v: PlanVendor[]) => updatePlan(plan.id, { vendors: v });

  const update = (id: string, changes: Partial<PlanVendor>) =>
    save(vendors.map((v) => (v.id === id ? { ...v, ...changes } : v)));

  const remove = (id: string) => save(vendors.filter((v) => v.id !== id));

  const addVendor = () => {
    if (!newVendor.name.trim()) return;
    save([...vendors, { ...newVendor, id: crypto.randomUUID() }]);
    setNewVendor(EMPTY_VENDOR);
    setAddingRow(false);
  };

  const columns: ColumnsType<PlanVendor> = [
    {
      title: "Vendor",
      key: "name",
      width: 180,
      render: (_, v) => (
        <InlineEdit
          value={v.name}
          onSave={(val) => update(v.id, { name: val })}
          placeholder="Vendor name"
          width={160}
        />
      ),
    },
    {
      title: "Type",
      key: "type",
      width: 140,
      render: (_, v) => (
        <Select
          size="small"
          value={v.type}
          style={{ width: 130 }}
          options={VENDOR_TYPES.map((t) => ({ value: t, label: t }))}
          onChange={(val) => update(v.id, { type: val })}
          labelRender={({ value }) => (
            <span
              className={`text-[12px] font-semibold px-1.5 rounded-[3px] ${TYPE_STYLE[value as VendorType] ?? ""}`}
            >
              {value}
            </span>
          )}
        />
      ),
    },
    {
      title: "Services",
      key: "services",
      render: (_, v) => (
        <InlineEdit
          value={v.services}
          onSave={(val) => update(v.id, { services: val })}
          placeholder="Describe services…"
          width={240}
        />
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, v) => (
        <Select
          size="small"
          value={v.status}
          options={STATUS_OPTIONS}
          onChange={(val) => update(v.id, { status: val })}
          style={{ width: 122 }}
          labelRender={({ value }) => (
            <span
              className={`text-[12px] font-semibold capitalize px-1.5 rounded-[3px] ${STATUS_STYLE[value as VendorStatus] ?? ""}`}
            >
              {STATUS_OPTIONS.find((o) => o.value === value)?.label}
            </span>
          )}
        />
      ),
    },
    {
      title: "Contact",
      key: "contact",
      width: 160,
      render: (_, v) => (
        <InlineEdit
          value={v.contact}
          onSave={(val) => update(v.id, { contact: val })}
          placeholder="Name or email"
          width={150}
        />
      ),
    },
    {
      title: "Notes",
      key: "notes",
      render: (_, v) => (
        <InlineEdit
          value={v.notes}
          onSave={(val) => update(v.id, { notes: val })}
          placeholder="Add note…"
          width={200}
        />
      ),
    },
    {
      key: "actions",
      width: 40,
      align: "center",
      render: (_, v) => (
        <Popconfirm
          title="Remove this vendor?"
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={() => remove(v.id)}
        >
          <Button
            size="small"
            type="text"
            icon={<DeleteOutlined />}
            className="text-[#9CA3AF] hover:text-[#EF4444]"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div>
          <h2 className="text-[15px] font-semibold text-[#1F2933] m-0">
            CRO &amp; Vendors
          </h2>
          <p className="text-[12px] text-[#6B7280] mt-0.5">
            Track service providers and their status for this trial.
          </p>
        </div>
        <Button
          size="small"
          type="primary"
          icon={<PlusOutlined />}
          className="bg-[#0C98D0] border-none"
          onClick={() => setAddingRow(true)}
        >
          Add vendor
        </Button>
      </div>

      {addingRow && (
        <div className="bg-white rounded-[10px] border border-[#0C98D0]/30 p-4 mb-4 shrink-0">
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div>
              <p className="text-[12px] text-[#6B7280] mb-1">Vendor name *</p>
              <Input
                size="small"
                placeholder="e.g. ICON plc"
                value={newVendor.name}
                onChange={(e) =>
                  setNewVendor((v) => ({ ...v, name: e.target.value }))
                }
              />
            </div>
            <div>
              <p className="text-[12px] text-[#6B7280] mb-1">Type</p>
              <Select
                size="small"
                className="w-full"
                value={newVendor.type}
                options={VENDOR_TYPES.map((t) => ({ value: t, label: t }))}
                onChange={(val) => setNewVendor((v) => ({ ...v, type: val }))}
              />
            </div>
            <div>
              <p className="text-[12px] text-[#6B7280] mb-1">Status</p>
              <Select
                size="small"
                className="w-full"
                value={newVendor.status}
                options={STATUS_OPTIONS}
                onChange={(val) => setNewVendor((v) => ({ ...v, status: val }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <p className="text-[12px] text-[#6B7280] mb-1">Services</p>
              <Input
                size="small"
                placeholder="Describe services provided"
                value={newVendor.services}
                onChange={(e) =>
                  setNewVendor((v) => ({ ...v, services: e.target.value }))
                }
              />
            </div>
            <div>
              <p className="text-[12px] text-[#6B7280] mb-1">Contact</p>
              <Input
                size="small"
                placeholder="Name or email"
                value={newVendor.contact}
                onChange={(e) =>
                  setNewVendor((v) => ({ ...v, contact: e.target.value }))
                }
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              size="small"
              onClick={() => {
                setAddingRow(false);
                setNewVendor(EMPTY_VENDOR);
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              type="primary"
              className="bg-[#0C98D0] border-none"
              disabled={!newVendor.name.trim()}
              onClick={addVendor}
            >
              Add vendor
            </Button>
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 rounded-[8px] border border-[#F0F2F4] bg-white overflow-hidden">
        {vendors.length === 0 && !addingRow ? (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-[14px] text-[#6B7280] text-center px-6">
              No vendors added yet. Add CROs, technology providers, central
              labs, and other service partners for this trial.
            </p>
            <Button
              size="small"
              type="primary"
              className="mt-3 bg-[#0C98D0] border-none"
              icon={<PlusOutlined />}
              onClick={() => setAddingRow(true)}
            >
              Add first vendor
            </Button>
          </div>
        ) : (
          <Table<PlanVendor>
            dataSource={vendors}
            columns={columns}
            rowKey="id"
            size="small"
            scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
            pagination={false}
          />
        )}
      </div>
    </div>
  );
}
